import { useEffect, useRef, type RefObject } from 'react';

/**
 * Where a chat transcript scrolls, and when: only when a message is ADDED,
 * never while an answer streams.
 *
 * Following every streamed frame pinned the view to the bottom — a reader could
 * not scroll back up to the prose while the tables under it arrived, and three
 * tables pushed the prose off the screen entirely. So a question just sent goes
 * to the top of the view and its answer grows down from it, at the reader's
 * pace; a thread opened from the list goes to its end.
 *
 * It scrolls `scroller` and nothing else. `scrollIntoView` moves every
 * scrollable ancestor up to the window, so a question sent from the dashboard
 * card dragged the whole dashboard with it.
 *
 * Shared by the full page and the dashboard card so the two cannot drift.
 */
export function useTranscriptScroll(
  messages: readonly { role: string }[],
  isStreaming: boolean,
  scrollerRef: RefObject<HTMLElement | null>,
) {
  const questionRef = useRef<HTMLDivElement>(null);
  const seen = useRef(0);
  const count = messages.length;

  useEffect(() => {
    // A streamed frame, a rating, the turn ending: the count is unchanged, and
    // the view stays where the reader put it.
    if (count === seen.current) return;
    seen.current = count;
    // Both are mounted by the time a message is added: the scroller holds the
    // transcript, and a turn in flight always has its question row.
    const scroller = scrollerRef.current!;
    const top = isStreaming
      ? scroller.scrollTop +
        questionRef.current!.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top
      : scroller.scrollHeight;
    scroller.scrollTo({ top, behavior: 'smooth' });
  }, [count, isStreaming, scrollerRef]);

  return {
    questionRef,
    /** The newest question — the message `questionRef` belongs on. */
    questionAt: messages.map((m) => m.role).lastIndexOf('user'),
  };
}
